import {
    NuveiWithdrawalDto,
    NuveiDepositDto,
    OnlineTransactionDto,
    OnlineTransactionDtoStatus,
    TransactionDtoType,
    TransaksDepositDto,
} from '@core/mappers/transaction/dtos/transactionDtos';
import {
    OnlineTransaction,
    OnlineTransactionProvider,
    PaymentMeans,
} from '@core/mappers/transaction/types';
import { PaymentProvider } from '@walletFederation/src/modules/payments/enums';
import { TransactionMethod } from '@core/services/antiFraud/types';
import {
    PaymentStatus,
    TransactionType,
} from '@superbet-group/web.types';

const pixProviderMap: Record<PaymentProvider, OnlineTransactionProvider> = {
    [PaymentProvider.Nuvei]: TransactionMethod.SafeCharge,
    [PaymentProvider.Okto]: TransactionMethod.Okto,
    [PaymentProvider.Paybrokers]: TransactionMethod.Paybrokers,
};

export function transformOnlineTransaction(transactionDto: OnlineTransactionDto): OnlineTransaction {
    let status: PaymentStatus;
    switch (transactionDto.kind) {
        case TransactionDtoType.BankWithdrawal:
            return {
                kind: TransactionType.Withdrawal,
                id: transactionDto.transactionCode,
                amount: transactionDto.amount,
                provider: TransactionMethod.Bank,
                method: TransactionMethod.Bank,
                status: transactionDto.status,
                shouldBeReported: true,
            };
        case TransactionDtoType.MonriDeposit:
            // Monri payload is only received currently from a success page so it cannot fail
            // so we are manually setting success
            return {
                kind: TransactionType.Deposit,
                id: transactionDto.order_number,
                amount: transactionDto.amount,
                provider: TransactionMethod.Monri,
                method: transactionDto.acquirer,
                status: PaymentStatus.Success,
                shouldBeReported: true,
                transactionDto,
            };
        case TransactionDtoType.NuveiDeposit:
            status = mapTransactionStatus(transactionDto.transactionStatus);
            return {
                kind: TransactionType.Deposit,
                id: transactionDto.PPP_TransactionID,
                provider: getNuveiTransactionProvider(transactionDto),
                status,
                amount: transactionDto.totalAmount,
                method: transactionDto.payment_method,
                shouldBeReported: getTransactionShouldBeReported(status, transactionDto.payment_method),
                transactionDto,
            };
        case TransactionDtoType.PixDeposit:
            status = mapTransactionStatus(transactionDto.transactionStatus);
            return {
                kind: TransactionType.Deposit,
                id: transactionDto.PPP_TransactionID,
                provider: pixProviderMap[transactionDto.provider],
                status,
                amount: transactionDto.totalAmount,
                method: transactionDto.payment_method,
                shouldBeReported: getTransactionShouldBeReported(status, transactionDto.payment_method),
                transactionDto,
            };
        case TransactionDtoType.TransaksDeposit:
            status = mapTransaksTransactionStatus(transactionDto.response);
            return {
                kind: TransactionType.Deposit,
                id: transactionDto.payment_id,
                provider: TransactionMethod.Transaks,
                status,
                amount: transactionDto.amount,
                method: TransactionMethod.Transaks,
                shouldBeReported: true,
                transactionDto,
            };
        case TransactionDtoType.PixWithdrawal:
            status = mapTransactionStatus(transactionDto.transactionStatus ?? transactionDto.status);
            return {
                kind: TransactionType.Withdrawal,
                id: transactionDto.wd_id ?? transactionDto.transactionCode!,
                provider: pixProviderMap[transactionDto.provider],
                status,
                amount: transactionDto.wd_amount ?? transactionDto.amount!,
                method: transactionDto.payment_method,
                shouldBeReported: getTransactionShouldBeReported(status, transactionDto.payment_method),
            };
        default:
            // TODO: Split this in NuveiWithdrawal and PaysafeCardWithdrawal
            status = mapTransactionStatus(transactionDto.transactionStatus ?? transactionDto.status);
            return {
                kind: TransactionType.Withdrawal,
                id: transactionDto.wd_id ?? transactionDto.transactionCode!,
                provider: getNuveiTransactionProvider(transactionDto),
                status,
                amount: transactionDto.wd_amount ?? transactionDto.amount!,
                method: transactionDto.payment_method,
                shouldBeReported: getTransactionShouldBeReported(status, transactionDto.payment_method),
            };
    }
}

// eslint-disable-next-line class-methods-use-this
function mapTransactionStatus(transactionDtoStatus?: OnlineTransactionDtoStatus): PaymentStatus {
    const transactionStatus = transactionDtoStatus?.toLowerCase();

    switch (transactionStatus) {
        case 'success':
            return PaymentStatus.Success;
        case 'failed':
            return PaymentStatus.Failed;
        case 'cancel':
            return PaymentStatus.Cancel;
        case 'pending':
            return PaymentStatus.Pending;
        default:
            return PaymentStatus.Failed;
    }
}

function mapTransaksTransactionStatus(transactionDtoStatus: TransaksDepositDto['response']): PaymentStatus {
    const transactionStatus = transactionDtoStatus.toLowerCase();

    if (transactionStatus === 'successful') {
        return PaymentStatus.Success;
    } else {
        return PaymentStatus.Failed;
    }
}

function getNuveiTransactionProvider(transactionDto: NuveiDepositDto | NuveiWithdrawalDto) {
    const isTransactionMethodSafeCharge = transactionDto.kind === TransactionDtoType.NuveiDeposit
        ? transactionDto.payment_method === PaymentMeans.Card
        : !!transactionDto.wd_id;
    return isTransactionMethodSafeCharge ? TransactionMethod.SafeCharge : TransactionMethod.PaySafe;
}

function getTransactionShouldBeReported(transactionStatus: PaymentStatus, transactionMethod: string): boolean {
    return transactionStatus === PaymentStatus.Success
        || (transactionStatus === PaymentStatus.Pending && transactionMethod !== PaymentMeans.Neteller);
}
